// Auth store — manages JWT token & user state
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/components/common/Providers';
import { User, LoginResponse } from '@/types';
import apiClient, { API_URL, getApiPrefix, getLoginBasePath, resetAuthExpired } from '@/lib/api';

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  permissions: string[];
  isSuperuser: boolean;
  inMarketSales: boolean;
  _hydrated: boolean;

  login: (email: string, password: string, deviceInfo?: { device_id: string; fingerprint: Record<string, unknown>; device_name?: string }) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<void>;
  setUser: (user: User) => void;
  fetchPermissions: () => Promise<void>;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  hasAllPermissions: (codes: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      loading: false,
      permissions: [],
      isSuperuser: false,
      inMarketSales: false,
      _hydrated: false,

      login: async (email: string, password: string, deviceInfo?: { device_id: string; fingerprint: Record<string, unknown>; device_name?: string }) => {
        set({ loading: true });
        try {
          const res = await apiClient.post<LoginResponse>('/auth/login', {
            email,
            password,
            ...(deviceInfo || {}),
          });
          const data = res.data;
          set({
            token: data.access_token,
            user: data.user,
            isAuthenticated: true,
            loading: false,
          });
          resetAuthExpired();   // 重新登录成功：恢复 401 处理，允许会话下次过期时再提示一次
          // 登录后立即用原生 fetch 加载权限（不用 apiClient，避免 axios interceptor
          // 从 localStorage 读 token 的竞争条件）
          try {
            const permRes = await fetch(`${API_URL}/auth/my-permissions`, {
              headers: { Authorization: `Bearer ${data.access_token}` },
            });
            if (permRes.ok) {
              const permData = await permRes.json();
              set({ permissions: permData.permissions, isSuperuser: permData.is_superuser, inMarketSales: permData.in_market_sales });
            }
          } catch {
            // 权限加载失败不影响登录
          }
        } catch (err: any) {
          set({ loading: false });
          throw new Error(err.response?.data?.detail || '登录失败');
        }
      },

      logout: () => {
        set({ token: null, user: null, isAuthenticated: false, permissions: [], isSuperuser: false });
        queryClient.clear();
      },

      fetchMe: async () => {
        const { token } = get();
        if (!token) return;
        try {
          const res = await apiClient.get<User>('/auth/me');
          set({ user: res.data, isAuthenticated: true });
        } catch {
          set({ token: null, user: null, isAuthenticated: false, permissions: [], isSuperuser: false });
          // token 无效，跳转登录页
          if (typeof window !== 'undefined') {
            const base = getLoginBasePath();
            window.location.href = `${base}/login`;
          }
        }
      },

      setUser: (user: User) => set({ user }),

      fetchPermissions: async () => {
        const { token } = get();
        if (!token) return;
        try {
          const res = await apiClient.get<{ permissions: string[]; is_superuser: boolean; in_market_sales: boolean }>('/auth/my-permissions');
          set({ permissions: res.data.permissions, isSuperuser: res.data.is_superuser, inMarketSales: res.data.in_market_sales });
        } catch {
          // Silent fail — permissions default to empty
        }
      },

      hasPermission: (code: string) => {
        const { isSuperuser, permissions } = get();
        if (isSuperuser) return true;
        return permissions.includes(code);
      },

      hasAnyPermission: (codes: string[]) => {
        const { isSuperuser, permissions } = get();
        if (isSuperuser) return true;
        return codes.some(c => permissions.includes(c));
      },

      hasAllPermissions: (codes: string[]) => {
        const { isSuperuser, permissions } = get();
        if (isSuperuser) return true;
        return codes.every(c => permissions.includes(c));
      },
    }),
    {
      name: 'rwai-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        permissions: state.permissions,
        isSuperuser: state.isSuperuser,
        _hydrated: true,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            state._hydrated = true;
          }
          // 检查 token 是否过期：异步验证 token 有效性
          let retried = false;   // 5xx 时只补校验一次，不做无限重试
          const clearAuth = () => {
            try { localStorage.removeItem('rwai-auth'); } catch { /* ignore */ }
            useAuthStore.setState({ token: null, user: null, isAuthenticated: false, permissions: [], isSuperuser: false });
          };
          const verify = async (): Promise<void> => {
            try {
              const raw = localStorage.getItem('rwai-auth');
              if (!raw) return;
              const parsed = JSON.parse(raw);
              const token = parsed?.state?.token;
              if (!token) { clearAuth(); return; }
              // 用原生 fetch 验证 token，避免 axios interceptor 循环
              const prefix = typeof window !== 'undefined'
                ? (() => {
                    const p = window.location.pathname;
                    if (p.startsWith('/rwai-prod')) return '/rwai-prod/api';
                    if (p.startsWith('/rwai')) return '/rwai/api';
                    return '/api';  // 域名直接访问
                  })()
                : '/rwai/api';
              const res = await fetch(`${prefix}/auth/me`, {
                headers: { 'Authorization': 'Bearer ' + token },
              });
              // ⚠️ 只有明确的 401（token 真的无效/过期）才登出。
              //    以前写的是 if (!res.ok)：后端重启/网关抖动那几秒返回的 5xx 会把用户直接踢回登录页
              //    （表现就是"会话莫名掉了"）。非 401 一律保留登录态。
              if (res.status === 401) { clearAuth(); return; }
              if (!res.ok) {
                useAuthStore.setState({ _hydrated: true });
                if (!retried) {   // 页面刚好开在后端重启窗口 → 3 秒后补一次
                  retried = true;
                  setTimeout(() => { void verify(); }, 3000);
                }
              }
            } catch {
              // 网络错误 → 不清除 token，让 AppLayout 的路由守卫兜底
              // 但不管怎样 mark rehydrated
              useAuthStore.setState({ _hydrated: true });
            }
          };
          void verify();
        };
      },
    }
  )
);
